import { Link } from 'react-router-dom';
import { Church, LayoutDashboard, ListChecks, Printer, ArrowRight, Wallet, PieChart, Send, ShieldCheck } from 'lucide-react';

const HOW_STEPS = [
  {
    icon: ShieldCheck,
    title: '1. Sign in securely',
    body: 'Only church admins with credentials can access the ledger. Your data stays on this device.',
  },
  {
    icon: LayoutDashboard,
    title: '2. Read the Dashboard',
    body: 'See total net worth, February spending, recent tithes and expenses at a single glance.',
  },
  {
    icon: ListChecks,
    title: '3. Update your Categories',
    body: 'Adjust budgets for every bill — Pastor salary, utilities, kitchen, Sunday School and more.',
  },
  {
    icon: Printer,
    title: '4. Print a Balance Sheet',
    body: 'Generate a clean, professional report to share at deacon and business meetings.',
  },
];

const SECTIONS = [
  { to: '/app/dashboard', label: 'Dashboard', icon: LayoutDashboard, desc: 'Net worth, transactions, spend summary' },
  { to: '/app/categories', label: 'Categories', icon: ListChecks, desc: 'Update every bill & monthly budget' },
  { to: '/app/balance-sheet', label: 'Balance Sheet', icon: Printer, desc: 'Professional printable report' },
];

const COMING = [
  { icon: Wallet, label: 'Accounts / Wallet' },
  { icon: PieChart, label: 'Budget Insights' },
  { icon: Send, label: 'Payments & Transfer' },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[var(--gcbc-bg)] relative overflow-x-hidden" data-testid="landing-page">
      {/* Hovering nav */}
      <div className="fixed top-4 left-0 right-0 z-50 px-3 sm:px-6">
        <div className="max-w-6xl mx-auto gcbc-glass-nav rounded-2xl px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white" data-testid="landing-brand">
            <div className="w-9 h-9 rounded-xl bg-white/15 border border-white/25 flex items-center justify-center">
              <Church className="w-4 h-4 text-white" />
            </div>
            <div className="leading-tight">
              <div className="font-display text-sm sm:text-base font-bold tracking-tight">Grants Creek Baptist Church</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-blue-100">Finances · 2026</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            <a href="#how" className="px-3 py-2 rounded-full text-sm text-white/90 hover:bg-white/15 transition-colors">How it works</a>
            <a href="#sections" className="px-3 py-2 rounded-full text-sm text-white/90 hover:bg-white/15 transition-colors">Sections</a>
            <Link
              to="/login"
              data-testid="landing-signin-nav"
              className="ml-2 px-4 py-2 rounded-full bg-white text-[var(--gcbc-royal-deep)] font-semibold text-sm hover:bg-blue-50 transition-colors"
            >
              Sign in
            </Link>
          </nav>
          <Link
            to="/login"
            className="md:hidden px-3 py-1.5 rounded-full bg-white text-[var(--gcbc-royal-deep)] font-semibold text-xs"
            data-testid="landing-signin-nav-mobile"
          >
            Sign in
          </Link>
        </div>
      </div>

      {/* Hero */}
      <section className="pt-32 sm:pt-40 pb-16 relative">
        <div
          className="absolute inset-0 -z-10"
          style={{
            background:
              'radial-gradient(1200px 500px at 15% 10%, rgba(29,78,216,0.14), transparent 60%), radial-gradient(900px 400px at 90% 20%, rgba(225,29,72,0.10), transparent 60%)',
          }}
          aria-hidden
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span
              data-testid="landing-eyebrow"
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-slate-200 text-xs font-semibold text-[var(--gcbc-royal-deep)] uppercase tracking-widest shadow-sm"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--gcbc-red)]"></span>
              Church Financial Stewardship
            </span>
            <h1 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] text-slate-900">
              Every offering,
              <br />
              <span className="relative inline-block">
                <span className="relative z-10 text-[var(--gcbc-royal-deep)]">accounted for.</span>
                <span className="absolute inset-x-0 bottom-1 h-3 bg-red-100 -z-0"></span>
              </span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl">
              A colorful, easy-to-use ledger for Grants Creek Baptist Church. Track tithes, monitor bills,
              set category budgets and print a professional balance sheet for every business meeting.
              Built with the treasurer&apos;s workflow in mind.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/login"
                data-testid="landing-cta-primary"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[var(--gcbc-royal)] text-white font-semibold shadow-lg shadow-blue-900/20 hover:bg-[var(--gcbc-royal-deep)] transition-colors"
              >
                Open the ledger <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="#how"
                data-testid="landing-cta-secondary"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-[var(--gcbc-royal-deep)] font-semibold border border-slate-200 hover:border-[var(--gcbc-royal)] transition-colors"
              >
                How to use
              </a>
            </div>
            <div className="mt-8 flex flex-wrap gap-6 text-sm text-slate-600">
              <div><span className="font-bold text-slate-900">14</span> tracked categories</div>
              <div><span className="font-bold text-slate-900">1-click</span> printable balance sheet</div>
              <div><span className="font-bold text-slate-900">Local</span> device storage — private</div>
            </div>
          </div>

          {/* Preview card */}
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-3 bg-gradient-to-br from-blue-200 via-white to-red-100 rounded-3xl blur-2xl opacity-70" aria-hidden></div>
              <div className="relative gcbc-accent-top rounded-2xl bg-white shadow-xl border border-slate-200 p-6">
                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Net Worth (Feb)</div>
                  <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-emerald-100 text-emerald-700">+3.4%</span>
                </div>
                <div className="mt-2 font-display text-4xl font-black text-[var(--gcbc-royal-deep)]">$84,331.30</div>
                <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                  {[
                    { label: 'Income', v: '$6,670' },
                    { label: 'Expenses', v: '$4,368' },
                    { label: 'Saved', v: '$2,302' },
                  ].map((s) => (
                    <div key={s.label} className="rounded-xl bg-slate-50 border border-slate-200 py-2.5">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">{s.label}</div>
                      <div className="text-sm font-bold text-slate-900 mt-0.5">{s.v}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-5 space-y-2">
                  {[
                    { c: 'Pastor Salary', p: 100, red: false },
                    { c: 'Electric Bill', p: 92, red: false },
                    { c: 'Kitchen', p: 87, red: false },
                    { c: 'Special Events', p: 64, red: true },
                  ].map((r) => (
                    <div key={r.c} className="flex items-center gap-3">
                      <div className="w-28 text-xs text-slate-600">{r.c}</div>
                      <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${r.p}%`,
                            background: r.red ? 'linear-gradient(90deg,#ef4444,#b91c1c)' : 'linear-gradient(90deg,#3b82f6,#1d4ed8)',
                          }}
                        />
                      </div>
                      <div className="text-xs font-semibold text-slate-700 w-10 text-right">{r.p}%</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How to use */}
      <section id="how" className="py-16 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="text-xs uppercase tracking-widest text-[var(--gcbc-red-deep)] font-bold">How to use</div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-2">Four simple steps</h2>
            </div>
            <p className="text-slate-600 max-w-md text-sm">
              The app is designed so any deacon or treasurer can pick it up in minutes — no accounting background required.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {HOW_STEPS.map((s, i) => (
              <div
                key={s.title}
                data-testid={`how-step-${i + 1}`}
                className="gcbc-accent-top rounded-2xl bg-white border border-slate-200 p-6 hover:-translate-y-1 hover:shadow-lg transition-transform"
              >
                <div className="w-11 h-11 rounded-xl bg-blue-50 text-[var(--gcbc-royal-deep)] flex items-center justify-center">
                  <s.icon className="w-5 h-5" />
                </div>
                <div className="mt-4 font-display font-bold text-lg text-slate-900">{s.title}</div>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sections index */}
      <section id="sections" className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <div className="text-xs uppercase tracking-widest text-[var(--gcbc-red-deep)] font-bold">Explore</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-2">What&apos;s inside the app</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {SECTIONS.map((s) => (
              <Link
                key={s.to}
                to="/login"
                data-testid={`landing-section-${s.label.toLowerCase().replace(/\s+/g, '-')}`}
                className="group rounded-2xl bg-white border border-slate-200 p-6 hover:border-[var(--gcbc-royal)] transition-colors block"
              >
                <div className="flex items-center justify-between">
                  <div className="w-11 h-11 rounded-xl bg-[var(--gcbc-royal)] text-white flex items-center justify-center">
                    <s.icon className="w-5 h-5" />
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-[var(--gcbc-royal)] group-hover:translate-x-1 transition-transform" />
                </div>
                <div className="mt-4 font-display text-xl font-bold text-slate-900">{s.label}</div>
                <div className="text-sm text-slate-600 mt-1">{s.desc}</div>
              </Link>
            ))}
          </div>

          <div className="mt-8 rounded-2xl border border-dashed border-slate-300 bg-white p-6">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs uppercase tracking-widest font-bold text-slate-500">Coming next</span>
              {COMING.map((c) => (
                <span
                  key={c.label}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200 text-sm text-slate-700"
                >
                  <c.icon className="w-4 h-4 text-[var(--gcbc-royal)]" />
                  {c.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-2">
          <div className="text-sm text-slate-600">
            <span className="font-semibold text-[var(--gcbc-royal-deep)]">GCBC finances app 2026</span>
            <span className="mx-2 text-slate-300">·</span>
            Grants Creek Baptist Church
          </div>
          <div className="text-xs text-slate-500">Stewarding what the Lord provides · Psalm 24:1</div>
        </div>
      </footer>
    </div>
  );
}
